import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <footer className="bg-background-dark text-white border-t border-white/10 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <motion.div variants={itemVariants} className="space-y-6">
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              onClick={() => {
                if (window.location.pathname === '/') {
                  window.location.reload();
                }
              }}
            >
              <div className="size-12 flex items-center justify-center bg-primary/20 rounded-xl border border-primary/30 text-primary group-hover:bg-primary group-hover:text-black transition-all duration-300">
                <span className="material-symbols-outlined text-3xl">apartment</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Sunset</h2>
                <p className="text-primary text-xs font-semibold uppercase tracking-widest">Apartments</p>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3">
              {[
                { name: 'instagram', url: 'https://instagram.com' },
                { name: 'facebook', url: 'https://facebook.com' }
              ].map((social) => (
                <motion.a 
                  key={social.name}
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-black transition-all duration-300 border border-white/10 hover:border-primary" 
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {social.name === 'instagram' && (
                    <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path>
                    </svg>
                  )}
                  {social.name === 'facebook' && (
                    <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
                    </svg>
                  )}
                </motion.a>
              ))}
            </div>
          </motion.div>
          
          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-primary rounded-full" />
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: t('nav.home') },
                { to: '/about', label: t('nav.about') },
                { to: '/properties', label: t('nav.properties') },
                { to: '/contact', label: t('nav.contact') },
              ].map((link) => (
                <li key={link.label}>
                  <Link 
                    className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2 group text-sm" 
                    to={link.to}
                  >
                    <span className="material-symbols-outlined text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">chevron_right</span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
          
          {/* Contact */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-primary rounded-full" />
              {t('footer.contactUs')}
            </h3>
            <ul className="space-y-4">
              {[
                { icon: 'location_on', text: t('footer.address') },
                { icon: 'call', text: t('common.phone') },
                { icon: 'mail', text: t('common.email') },
                { icon: 'schedule', text: t('footer.workingHours') },
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-400 text-sm group">
                  <span className="material-symbols-outlined text-primary mt-0.5 group-hover:scale-110 transition-transform">{item.icon}</span>
                  <span className="whitespace-pre-line">{item.text}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          
          {/* Stay Connected */}
          <motion.div variants={itemVariants} className="space-y-6">
            <h3 className="text-white text-lg font-bold flex items-center gap-2">
              <span className="w-8 h-0.5 bg-primary rounded-full" />
              {t('footer.stayConnected')}
            </h3>
            <p className="text-gray-400 text-sm">{t('footer.stayConnectedText')}</p>
            <form className="space-y-3">
              <div className="relative group">
                <input 
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl pl-4 pr-14 py-3.5 focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-gray-500 transition-all text-sm" 
                  placeholder="Email" 
                  type="email"
                />
                <motion.button 
                  whileHover={{ backgroundColor: "rgba(212,175,53,0.9)" }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-primary text-black rounded-lg transition-all duration-200 flex items-center justify-center" 
                  type="button"
                >
                  <motion.span 
                    className="material-symbols-outlined text-lg"
                    whileHover={{ x: 2 }}
                    whileTap={{ x: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    arrow_forward
                  </motion.span>
                </motion.button>
              </div>
            </form>
            
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="material-symbols-outlined text-primary text-lg">verified</span>
                {t('footer.verifiedHost')}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="material-symbols-outlined text-primary text-lg">shield</span>
                {t('footer.secureBooking')}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Bottom bar */}
      <div className="border-t border-white/10 bg-background-dark/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">{t('footer.copyright')}</p>
            <div className="flex gap-6 text-sm text-gray-500">
              <Link className="hover:text-primary transition-colors" to="/privacy">{t('footer.privacyPolicy')}</Link>
              <Link className="hover:text-primary transition-colors" to="/terms">{t('footer.termsOfService')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
